import { FC, useMemo, useRef } from 'react';
import Link from 'next/link';
import { gtmMenuNavigation } from 'helpers/utils/gtm-events/gtmInteraction';
import AmplienceNavColumn, { AmplienceNavColumnProps } from '../nav-column';

export interface AmplienceMegaNavProps {
  title?: string;
  link?: string;
  navColumns: AmplienceNavColumnProps[];
  isExpanded?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
}

const AmplienceMegaNav: FC<AmplienceMegaNavProps> = ({ link, navColumns, title, isExpanded, onExpand, onCollapse }) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  const columnsWithVisibility = useMemo(() => {
    const seenTitles = new Set<string>();

    return (navColumns ?? []).map((navColumn) => {
      const hideTitle = Boolean(navColumn.mainTitle && seenTitles.has(navColumn.mainTitle));
      if (navColumn.mainTitle) seenTitles.add(navColumn.mainTitle);
      return { navColumn, hideTitle };
    });
  }, [navColumns]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (!isExpanded) {
        event.preventDefault();
        onExpand?.();
      }
    } else if (event.key === 'Escape') {
      onCollapse?.();
      linkRef.current?.focus();
    }
  };

  return (
    <li
      className="group flex grow items-center justify-center border-b-4 border-transparent py-4 after:inline-block after:h-[21px] after:w-px after:bg-[rgb(0,0,0)]/30 after:content-[''] after:last:hidden hover:border-[#B19A6A] focus:border-transparent focus:ring-0"
      onKeyDown={handleKeyDown}
    >
      <Link
        ref={linkRef}
        onClick={() => {
          gtmMenuNavigation({ click_location: 'header', click_name: title ?? '' });
        }}
        className="flex w-full items-center justify-center px-2 text-center font-main text-[13px] font-semibold leading-4 text-[#222222]"
        href={link || ''}
        aria-expanded={isExpanded}
        onMouseEnter={onExpand}
        onMouseLeave={onCollapse}
        aria-label={title}
      >
        {title}
      </Link>
      {navColumns && navColumns.length > 0 && (
        <div
          className="absolute left-0 top-full z-[9] h-auto w-full bg-[#fcfcfa] px-6 shadow-[0px_4px_4px_rgba(177,154,106,0.3)] transition-all group-hover:block"
          hidden={!isExpanded}
        >
          <section className="mx-auto grid max-w-desktop">
            <section className="flex justify-evenly px-3 pb-12 pt-8">
              {columnsWithVisibility.map(({ navColumn, hideTitle }, index) => (
                <AmplienceNavColumn
                  key={`nav-column-${title}-${navColumn.mainTitle}-${index}`}
                  {...navColumn}
                  hideTitle={hideTitle}
                />
              ))}
            </section>
          </section>
        </div>
      )}
    </li>
  );
};

export default AmplienceMegaNav;
